import { Heading } from "@amzn/storm-ui";
import { useAsync } from "@react-hookz/web";
import React, { useEffect, useMemo } from "react";
import * as R from "remeda";
import { useStore } from "zustand";

import { useGetRequestParameters } from "../../../hooks/dimension/dimension.hooks";
import { useDimensionHydration } from "../../../store/dimension.hydration";
import {
    DimensionStatus,
    dimensionStatusStore,
} from "../../../store/dimension.status";
import type {
    DimensionConfiguration,
    DimensionTemplate,
} from "../../../types/dimensions";
import "react-json-view-lite/dist/index.css";
import { ComponentLabel } from "../../common/styled.components";

import { processDimensionStatus } from "./DataDimensionHelpers";
import type { DataSourceConfiguration } from "./datasources";
import { DataSourceGetterMap } from "./datasources";
import { DataDimensionValueType } from "./value/dimension.data.value";
import type { DataDimensionValueConfig } from "./value/dimension.data.value.factory";
import { getDataDimensionValueHandler } from "./value/dimension.data.value.factory";
import type { VisualizationConfiguration } from "./visualization/formatHandler";
import {
    ErrorVisualization,
    VisualizationComponentMap,
    VisualizationTypes,
} from "./visualization/formatHandler";

export interface DataDimensionConfiguration extends DimensionConfiguration {
    datasource: DataSourceConfiguration;
    visualization?: VisualizationConfiguration;
    value?: DataDimensionValueConfig;
}

export interface DataDimensionProps extends DimensionTemplate {
    configuration: DataDimensionConfiguration;
}

export const DataDimension: React.FunctionComponent<DataDimensionProps> = (
    props,
) => {
    const { optional, configuration, id, dependencies = [], debug } = props;
    const [setStatus, dependenciesCheck] = useStore(
        dimensionStatusStore,
        (state) => [state.setStatus, state.checkStatusSuccess(dependencies)],
    );

    const VisComponent =
        VisualizationComponentMap[
            configuration.visualization?.type ?? VisualizationTypes.NONE
        ] ?? ErrorVisualization;
    const getDataFunction = DataSourceGetterMap[configuration.datasource.type];

    const valueHandler = useMemo(() => {
        return getDataDimensionValueHandler(
            id,
            configuration.value ?? { type: DataDimensionValueType.NONE },
        );
    }, [id, configuration.value]);

    const resolvedDataRequirements = useGetRequestParameters(dependencies);

    // build requirements payload
    const dataPayload = useMemo(() => {
        const requirements = props.configuration.datasource.requirements || [];

        if (props.configuration.datasource.optionalDependencies) {
            return {
                status: "success",
                state: resolvedDataRequirements,
            };
        }

        if (!dependenciesCheck) {
            return {
                status: "waiting",
                message: "dependencies not ready",
                state: R.pipe(
                    requirements,
                    R.map((r) => ({ [r]: undefined })),
                    R.mergeAll,
                ) as any,
            };
        }
        const availableKeys = Object.keys(resolvedDataRequirements);
        const missingRequirements = R.pipe(
            requirements,
            R.map((r) => availableKeys.includes(r)),
        ).includes(false);
        if (missingRequirements) {
            return {
                status: "error",
                message: "missing requirements",
                state: R.pipe(
                    requirements,
                    R.map((r) => ({ [r]: undefined })),
                    R.mergeAll,
                ) as any,
            };
        } else {
            return {
                status: "success",
                state: R.pick(resolvedDataRequirements, requirements),
            };
        }
    }, [resolvedDataRequirements]);

    const [dataState, dataActions] = useAsync(async () => {
        setStatus(
            id,
            processDimensionStatus(DimensionStatus.LOADING, optional),
        );
        let data: any = {};
        try {
            data = await getDataFunction(
                props.configuration.datasource.settings,
                dataPayload.state,
            );
        } catch (error) {
            setStatus(
                id,
                processDimensionStatus(DimensionStatus.ERROR, optional),
            );
            throw error;
        }

        setStatus(
            id,
            processDimensionStatus(DimensionStatus.SUCCESS, optional),
        );
        return data;
    });

    const label = props.configuration.label;

    useEffect(() => {
        if (dataPayload.status === "success") {
            dataActions.reset();
            void dataActions.execute();
        }
    }, [JSON.stringify(Object.values(dataPayload.state))]);

    useEffect(() => {
        if (
            dataPayload.status === "success" &&
            dataState.status === "success"
        ) {
            valueHandler.setValue(dataState.result);
        }
    }, [dataState.status, dataState.result]);

    useDimensionHydration(id, dependencies, async () => {
        try {
            await dataActions.execute();
        } catch (err) {
            throw new Error("data dimension action failed");
        }
    });

    if (dataState.status === "success") {
        return (
            <>
                <VisComponent
                    {...props.configuration.visualization}
                    input={dataState.result}
                    label={label}
                />
                {debug && (
                    <div>
                        <Heading renderAs="h3">{id}</Heading>
                        Datasource
                        <div>
                            <code>{JSON.stringify(dataState.status)}</code>
                            <br />
                            <code>{JSON.stringify(dataState.result)}</code>
                        </div>
                    </div>
                )}
            </>
        );
    }

    if (dataState.status === "error") {
        return (
            <div>
                <div>
                    <ComponentLabel>{label}</ComponentLabel>
                </div>
                <code>Failed to fetch data: {dataState.error?.message}</code>
            </div>
        );
    }
    return <></>;
};
